import { useMemo } from "react";
import { createOptions, i18nCrossUtil, IS_WEB, kycCountrySelectOptionList } from "@adgm/libs";
import type { TTaxResidence } from "@adgm/cross-models";
import { CUSTOM_RADIO_WITH_INPUT, CUSTOM_RADIO_OTHER_VALUES } from "@adgm/cross-config";
import { colors } from "../../styles/common";

/**
 * 税务居住地数据接口
 */
export interface ITaxResidenceData extends TTaxResidence {
  id?: string;
}

/**
 * 组件 Props 接口
 */
export interface IKycTaxResidenceTableProps {
  value: ITaxResidenceData[];
  onChange?: (data: ITaxResidenceData[]) => void;
}

/**
 * 列配置创建器类型
 */
export type TColumnConfigCreator<T = any> = (type: string, options?: any) => T;

/**
 * hook 返回值接口
 */
export interface IUseKycTaxResidenceTableReturn<TColumnConfig = any> {
  /** 当前数据（带ID） */
  taxResidences: ITaxResidenceData[];
  /** 列配置 */
  taxResidenceColumns: TColumnConfig[];
  /** 数据变化处理函数 */
  handleChange: (data: any[]) => void;
  /** 国际化对象 */
  crossIntl: any;
}

/**
 * KYC 税务居住地表格公共逻辑 Hook
 *
 * @param props - 组件props
 * @param createRenderer - 渲染器创建函数
 * @returns 表格所需的所有逻辑和数据
 */
export function useKycTaxResidenceTable<TColumnConfig = any>(
  props: IKycTaxResidenceTableProps,
  createRenderer: TColumnConfigCreator,
): IUseKycTaxResidenceTableReturn<TColumnConfig> {
  const { value, onChange } = props;

  const crossIntl = i18nCrossUtil.t();

  // 定义表格的列配置（3列：country, tin, unavailableReason）
  const taxResidenceColumns = [
    {
      key: "country",
      title: crossIntl["kyc.v2.display.label.tax.residence.country"],
      dataIndex: "country",
      required: true,
      type: "select" as const,
      options: kycCountrySelectOptionList("commonName"),
      render: createRenderer("nationality"), // 表格显示使用文本渲染
    },
    {
      key: "tin",
      title: crossIntl["kyc.v2.display.label.tin"],
      dataIndex: "tin",
      required: false, // 去掉必填红点
      type: "text" as const,
      render: createRenderer("text", {
        defaultValue: crossIntl["kyc.v2.tax.unavailable.default.value"],
      }), // 空值时显示 "Unavailable"
      groupTitle: crossIntl["kyc.v2.display.label.tin.group.title"], // 分组标题，带必填标识
      mutuallyExclusiveWith: ["unavailableReason"], // 与 unavailableReason 互斥
      mutuallyExclusiveGroup: "tinOrReason", // 互斥组标识
    },
    {
      key: "unavailableReason",
      title: crossIntl["kyc.v2.display.label.reason.of.unavailability"],
      dataIndex: "unavailableReason",
      required: false, // 去掉必填红点
      type: "radioWithInput" as const,
      options: [
        ...createOptions([
          "The country/jurisdiction where the Account Holder is liable to pay tax and does not issue TINs to its residents.",
          "No TIN is required.\n(Note. Only select this reason if the domestic law of the relevant jurisdiction does not require the collection of the TIN issued by such jurisdiction)",
        ]),
        {
          title: crossIntl["kyc.other.input"],
          value: CUSTOM_RADIO_WITH_INPUT,
          description: crossIntl["kyc.v2.tax.unavailable.title"],
          inputStyle: {
            borderWidth: IS_WEB ? 0.5 : 1,
            borderColor: IS_WEB ? "rgba(0, 0, 0, 0.2)" : colors.textInputBorderColor,
          },
        },
      ],
      render: createRenderer("text", { defaultValue: "--" }), // 空值时显示 "--"
      placeholder: crossIntl["kyc.v2.display.label.tin.unavailable.reason.placeholder"],
      mutuallyExclusiveWith: ["tin"], // 与 tin 互斥
      mutuallyExclusiveGroup: "tinOrReason", // 互斥组标识
    },
  ];

  const taxResidencesValue = useMemo(() => {
    return value.map(item => {
      return {
        ...item,
      };
    });
  }, [value]);

  const handleChange = (data: any[]) => {
    // 转换回数据格式（country, tin, unavailableReason）
    const taxResidences = data.map(item => {
      // 如果值在 CUSTOM_RADIO_OTHER_VALUES 中，则转换为空字符串
      const normalizeValue = (value: any): string => {
        if (!value) return "";
        if (CUSTOM_RADIO_OTHER_VALUES.includes(value)) return "";
        return String(value);
      };

      return {
        country: item.country || "",
        tin: normalizeValue(item.tin),
        unavailableReason: normalizeValue(item.unavailableReason),
      };
    });
    onChange?.(taxResidences);
  };

  return {
    taxResidences: taxResidencesValue,
    taxResidenceColumns: taxResidenceColumns as TColumnConfig[],
    handleChange,
    crossIntl,
  };
}
